/**
 * Клиент Admin API: cookie-сессия + CSRF-токен в заголовке, ошибки — problem+json.
 * Если сервер просит повторное подтверждение (reauth_required), показывается диалог
 * пароля и кода, после чего запрос повторяется один раз.
 */

export const API_BASE = '/admin/v1';

export interface Problem {
  type?: string;
  title?: string;
  status: number;
  detail?: string;
  code: string;
  requestId?: string;
  errors?: { path: string; message: string }[];
  [key: string]: unknown;
}

export class ApiError extends Error {
  constructor(readonly problem: Problem) {
    super(problem.detail ?? problem.title ?? problem.code);
    this.name = 'ApiError';
  }

  get status(): number {
    return this.problem.status;
  }

  get code(): string {
    return this.problem.code;
  }
}

let csrfToken: string | undefined;
let reauthHandler: (() => Promise<boolean>) | undefined;
let unauthorizedHandler: (() => void) | undefined;

export function setCsrfToken(token: string | undefined): void {
  csrfToken = token;
}

export function onReauthRequired(handler: (() => Promise<boolean>) | undefined): void {
  reauthHandler = handler;
}

export function onUnauthorized(handler: (() => void) | undefined): void {
  unauthorizedHandler = handler;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** multipart/form-data (загрузка файлов). */
  form?: FormData;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Не повторять после reauth (внутренний флаг). */
  retried?: boolean;
}

export function buildUrl(path: string, query?: RequestOptions['query']): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return `${API_BASE}${path}${qs ? `?${qs}` : ''}`;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };
  let body: BodyInit | undefined;
  if (options.form) {
    body = options.form;
  } else if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
    body = JSON.stringify(options.body);
  }
  if (method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;

  const response = await fetch(buildUrl(path, options.query), {
    method,
    headers,
    body,
    credentials: 'same-origin',
  });
  if (response.status === 204) return undefined as T;

  const isJson = (response.headers.get('content-type') ?? '').includes('json');
  const data: unknown = isJson ? await response.json() : undefined;
  if (response.ok) return data as T;

  const problem: Problem =
    data && typeof data === 'object' && 'code' in data
      ? (data as Problem)
      : { status: response.status, code: `http_${String(response.status)}` };
  if (problem.code === 'reauth_required' && reauthHandler && !options.retried) {
    if (await reauthHandler()) return api<T>(path, { ...options, retried: true });
  }
  if (response.status === 401 && problem.code !== 'invalid_credentials') unauthorizedHandler?.();
  throw new ApiError(problem);
}

/** Сообщение для пользователя: ошибки валидации по полям или текст сервера. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const fields = error.problem.errors?.map((e) =>
      e.path ? `${e.path}: ${e.message}` : e.message,
    );
    if (fields?.length) return fields.join('\n');
    return error.message;
  }
  return error instanceof Error ? error.message : String(error);
}
